import { useCallback, useEffect, useState } from "react";

import { Dropdown } from "@magnetic/dropdown";
import { Flex } from "@magnetic/flex";
import { Positive } from "@magnetic/icons";
import { Popover } from "@magnetic/popover";
import { Tooltip } from "@magnetic/tooltip";

import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { Icon } from "src/components/icons";
import { ApiError } from "src/core/apiError";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import { GetInvitationsResponse } from "src/gen/schema/bilge/api";

import { PLATFORM_INVITATION_CONFIG } from "../constants";

interface ShareBotLinkProps {
  readonly kind?: "primary" | "secondary" | "tertiary";
}

interface BotLinkItem {
  readonly label: string;
  readonly value: string;
  readonly qaData: string;
  readonly platformKey: string;
}

export const ShareBotLink = ({ kind = "secondary" }: ShareBotLinkProps) => {
  const [botLinks, setBotLinks] = useState<BotLinkItem[]>([]);
  const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | undefined>();

  useEffect(() => {
    Bilge.getChatbotInvitations()
      .then((response: GetInvitationsResponse) => {
        const invitations = response.invitations || [];

        const links: BotLinkItem[] = invitations
          .map((invitation) => {
            const config = PLATFORM_INVITATION_CONFIG[invitation.chatPlatform];
            if (!config) {
              return null;
            }

            return {
              label: config.label,
              value: invitation.entry,
              qaData: config.qaData,
              platformKey: `${invitation.chatPlatform}-${invitation.entry}`
            };
          })
          .filter((link): link is BotLinkItem => link !== null);

        setBotLinks(links);
        setError(undefined);
      })
      .catch((error: ApiError) => {
        setError(error);
        setBotLinks([]);
      });
  }, []);

  const handleCopy = useCallback((platformKey: string, value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedPlatform(platformKey);
        // Auto-hide the "Copied" popover after 2 seconds
        setTimeout(() => setCopiedPlatform(null), 2000);
      })
      .catch((error) => {
        setError(ApiError.fromMessage(String(error)));
      });
  }, []);

  return (
    <HFlex justify="flex-end" className="bot-actions">
      <ErrorNotification error={error} />
      <Dropdown
        icon={<Icon name="share-bot-link" />}
        kind={kind}
        label="Share bot link"
        data-qa="share-bot-link-dropdown"
      >
        {botLinks.length === 0 ? (
          <Dropdown.Item
            label="No bot links available"
            data-qa="no-bot-links"
          />
        ) : (
          botLinks.map((item) => (
            <Popover
              key={item.platformKey}
              open={copiedPlatform === item.platformKey}
              setOpen={() => setCopiedPlatform(null)}
              placement="left"
              title={
                <Flex align="center" gap={4}>
                  <Positive />
                  Copied to clipboard
                </Flex>
              }
            >
              <Tooltip
                title="Copy to clipboard"
                placement="left"
                open={copiedPlatform === item.platformKey ? false : undefined}
              >
                <Dropdown.Item
                  label={item.label}
                  onClick={() => handleCopy(item.platformKey, item.value)}
                  data-qa={item.qaData}
                />
              </Tooltip>
            </Popover>
          ))
        )}
      </Dropdown>
    </HFlex>
  );
};
